import type { ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useTween } from '@/hooks/use-tween'
import { SliderField } from '@/components/landing/tools/slider-field'
import { ProjectionChart } from '@/components/landing/tools/projection-chart'

import {
    formatCurrency,
    formatDuration,
    formatPercent
} from '@/lib/format'
import {
    amortize,
    growthSeries,
    monthsToReach,
    niceCeiling,
    resample
} from '@/lib/finance'

const POINTS = 49
const money = (value: number) => formatCurrency(value)
const years = (value: number) => `${value} ${value === 1 ? 'year' : 'years'}`

interface ResultProps {
    label: string
    value: number | null
    text?: string
    highlight?: boolean
    note?: ReactNode
}

const Result = ({ label, value, text, highlight = false, note }: ResultProps) => {
    const animated = useTween(value ?? 0, { from: value ?? 0, duration: 650 })

    return (
        <div className={cn('rounded-[22px] p-5', highlight ? 'bg-citron/10 ring-1 ring-citron/25' : 'bg-white/[0.04] ring-1 ring-white/[0.06]')}>
            <p className='text-[11px] font-medium uppercase tracking-[0.22em] text-cream/50'>
                {label}
            </p>

            <p className={cn('mt-2 text-[1.9rem] font-normal leading-none tracking-[-0.04em] tabular-nums', highlight ? 'text-citron' : 'text-cream')}>
                {text ?? money(animated)}
            </p>

            {note && (
                <p className='mt-2 text-xs leading-relaxed text-cream/50'>
                    {note}
                </p>
            )}
        </div>
    )
}

interface LayoutProps {
    controls: ReactNode
    results: ReactNode
    chart: ReactNode
    insight: ReactNode
}

const CalculatorLayout = ({ controls, results, chart, insight }: LayoutProps) => {
    return (
        <div className='grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-10'>
            <div className='space-y-6'>
                {controls}
            </div>

            <div>
                <div className='grid gap-3 sm:grid-cols-3'>
                    {results}
                </div>

                <div className='mt-6 rounded-[24px] bg-white/[0.03] p-4 ring-1 ring-white/[0.06] sm:p-5'>
                    {chart}
                </div>

                <p className='mt-4 rounded-2xl bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-cream/75' aria-live='polite'>
                    {insight}
                </p>
            </div>
        </div>
    )
}

const horizonLabels = (months: number): [string, string, string] => {
    const half = Math.round(months / 2)

    return ['Today', `In ${formatDuration(half)}`, `In ${formatDuration(months)}`]
}

export const SavingsGoalCalculator = () => {
    const [goal, setGoal] = useState(25000)
    const [saved, setSaved] = useState(5000)
    const [monthly, setMonthly] = useState(600)
    const [rate, setRate] = useState(4)

    const result = useMemo(() => {
        const months = monthsToReach(saved, monthly, rate, goal)
        const faster = monthsToReach(saved, monthly + 100, rate, goal)
        const horizon = Number.isFinite(months) ? Math.max(6, months) : 360
        const series = growthSeries(saved, monthly, rate, horizon)
        const end = series[Math.min(series.length - 1, Number.isFinite(months) ? months : horizon)]
        const contributions = saved + monthly * (Number.isFinite(months) ? months : horizon)

        return { months, faster, horizon, series, contributions, growth: Math.max(0, end - contributions) }
    }, [goal, saved, monthly, rate])

    const reached = Number.isFinite(result.months)

    return (
        <CalculatorLayout
            controls={(
                <>
                    <SliderField id='savings-goal' label='What you are aiming for' value={goal} min={1000} max={200000} step={500} onChange={setGoal} format={money} />
                    <SliderField id='savings-saved' label='Put by so far' value={saved} min={0} max={100000} step={500} onChange={setSaved} format={money} />
                    <SliderField id='savings-monthly' label='Monthly saving' value={monthly} min={50} max={5000} step={25} onChange={setMonthly} format={money} />
                    <SliderField id='savings-rate' label='Annual interest or growth' value={rate} min={0} max={8} step={0.25} onChange={setRate} format={value => formatPercent(value, 2)} />
                </>
            )}
            results={(
                <>
                    <Result label='Time to get there' value={null} text={reached ? formatDuration(result.months) : '50+ years'} highlight />
                    <Result label='Your deposits' value={result.contributions} />
                    <Result label='Earned on top' value={result.growth} />
                </>
            )}
            chart={(
                <ProjectionChart
                    series={[{ label: 'Balance over time', values: resample(result.series, POINTS), area: true }]}
                    max={niceCeiling(Math.max(goal, ...result.series) * 1.05)}
                    target={goal}
                    targetLabel={`Target ${money(goal)}`}
                    xLabels={horizonLabels(result.horizon)}
                />
            )}
            insight={reached
                ? result.months === 0
                    ? 'You have already arrived. A good moment to choose what comes next.'
                    : `Put away ${money(monthly + 100)} a month and you arrive in ${formatDuration(result.faster)}, ${result.months - result.faster} ${result.months - result.faster === 1 ? 'month' : 'months'} earlier.`
                : 'At this rate the goal sits more than 50 years out. Raise the monthly amount or start with a smaller milestone.'}
        />
    )
}

export const RetirementCalculator = () => {
    const [age, setAge] = useState(34)
    const [retireAt, setRetireAt] = useState(65)
    const [saved, setSaved] = useState(40000)
    const [monthly, setMonthly] = useState(750)
    const [rate, setRate] = useState(6.5)

    const retireAge = Math.max(retireAt, age + 1)

    const result = useMemo(() => {
        const months = (retireAge - age) * 12
        const series = growthSeries(saved, monthly, rate, months)
        const contributionsSeries = series.map((_, month) => saved + monthly * month)
        const final = series[series.length - 1]
        const boosted = growthSeries(saved, monthly + 100, rate, months)

        return {
            months,
            series,
            contributionsSeries,
            final,
            contributed: contributionsSeries[contributionsSeries.length - 1],
            income: (final * 0.04) / 12,
            boost: boosted[boosted.length - 1] - final
        }
    }, [age, retireAge, saved, monthly, rate])

    return (
        <CalculatorLayout
            controls={(
                <>
                    <SliderField id='retire-age' label='How old you are now' value={age} min={20} max={65} step={1} onChange={setAge} format={years} />
                    <SliderField id='retire-at' label='Retirement age' value={retireAge} min={50} max={75} step={1} onChange={setRetireAt} format={years} />
                    <SliderField id='retire-saved' label='Set aside already' value={saved} min={0} max={1000000} step={1000} onChange={setSaved} format={money} />
                    <SliderField id='retire-monthly' label='Monthly contribution' value={monthly} min={0} max={5000} step={50} onChange={setMonthly} format={money} />
                    <SliderField id='retire-rate' label='Assumed annual return' value={rate} min={2} max={10} step={0.25} onChange={setRate} format={value => formatPercent(value, 2)} />
                </>
            )}
            results={(
                <>
                    <Result label={`By age ${retireAge}`} value={result.final} highlight />
                    <Result label='Income per month' value={result.income} note='Based on the 4% rule' />
                    <Result label='Your contributions' value={result.contributed} />
                </>
            )}
            chart={(
                <ProjectionChart
                    series={[
                        { label: 'Projected pot', values: resample(result.series, POINTS), area: true },
                        { label: 'Your own deposits', values: resample(result.contributionsSeries, POINTS), tone: 'muted', dashed: true }
                    ]}
                    max={niceCeiling(result.final * 1.05)}
                    xLabels={[`Age ${age}`, `Age ${Math.round((age + retireAge) / 2)}`, `Age ${retireAge}`]}
                />
            )}
            insight={`An extra ${money(100)} a month would add roughly ${money(Math.round(result.boost / 1000) * 1000)} by ${retireAge}. Time does the heavy lifting: ${formatPercent(Math.max(0, ((result.final - result.contributed) / Math.max(1, result.final)) * 100), 0)} of the final sum comes from growth rather than deposits.`}
        />
    )
}

export const LoanPayoffCalculator = () => {
    const [balance, setBalance] = useState(18000)
    const [rate, setRate] = useState(7.9)
    const [payment, setPayment] = useState(450)
    const [extra, setExtra] = useState(100)

    const result = useMemo(() => {
        const base = amortize(balance, rate, payment)
        const boosted = amortize(balance, rate, payment + extra)
        const horizon = Math.max(1, base.feasible ? base.months : boosted.months)
        const pad = (series: Array<number>) => [...series, ...Array.from({ length: Math.max(0, horizon + 1 - series.length) }, () => 0)].slice(0, horizon + 1)

        return { base, boosted, horizon, baseSeries: pad(base.series), boostedSeries: pad(boosted.series) }
    }, [balance, rate, payment, extra])

    const minimum = Math.ceil(balance * (rate / 100 / 12)) + 1

    if (!result.boosted.feasible) {
        return (
            <CalculatorLayout
                controls={(
                    <>
                        <SliderField id='loan-balance' label='Amount still owed' value={balance} min={1000} max={100000} step={500} onChange={setBalance} format={money} />
                        <SliderField id='loan-rate' label='Annual interest rate' value={rate} min={0} max={30} step={0.1} onChange={setRate} format={value => formatPercent(value, 1)} />
                        <SliderField id='loan-payment' label='Regular monthly payment' value={payment} min={50} max={3000} step={10} onChange={setPayment} format={money} />
                        <SliderField id='loan-extra' label='Extra payment each month' value={extra} min={0} max={1000} step={10} onChange={setExtra} format={money} />
                    </>
                )}
                results={<Result label='Paid off in' value={null} text='Not yet' highlight />}
                chart={<p className='py-16 text-center text-sm text-cream/55'>For now, each payment is smaller than the interest it has to cover.</p>}
                insight={`For the balance to start falling, the monthly payment has to be more than ${money(minimum)}.`}
            />
        )
    }

    const saved = result.base.feasible ? result.base.totalInterest - result.boosted.totalInterest : null
    const sooner = result.base.feasible ? result.base.months - result.boosted.months : null

    return (
        <CalculatorLayout
            controls={(
                <>
                    <SliderField id='loan-balance' label='Amount still owed' value={balance} min={1000} max={100000} step={500} onChange={setBalance} format={money} />
                    <SliderField id='loan-rate' label='Annual interest rate' value={rate} min={0} max={30} step={0.1} onChange={setRate} format={value => formatPercent(value, 1)} />
                    <SliderField id='loan-payment' label='Regular monthly payment' value={payment} min={50} max={3000} step={10} onChange={setPayment} format={money} />
                    <SliderField id='loan-extra' label='Extra payment each month' value={extra} min={0} max={1000} step={10} onChange={setExtra} format={money} />
                </>
            )}
            results={(
                <>
                    <Result label='Paid off in' value={null} text={formatDuration(result.boosted.months)} highlight />
                    <Result label='Interest you keep' value={saved} text={saved === null ? 'Every cent' : undefined} note={sooner ? `${sooner} ${sooner === 1 ? 'month' : 'months'} earlier` : undefined} />
                    <Result label='All interest paid' value={result.boosted.totalInterest} />
                </>
            )}
            chart={(
                <ProjectionChart
                    series={[
                        { label: 'Paying a little extra', values: resample(result.boostedSeries, POINTS), area: true },
                        { label: 'Regular payments only', values: resample(result.baseSeries, POINTS), tone: 'muted', dashed: true }
                    ]}
                    max={niceCeiling(balance * 1.05)}
                    xLabels={horizonLabels(result.horizon)}
                />
            )}
            insight={result.base.feasible
                ? extra > 0
                    ? `An extra ${money(extra)} a month clears the loan ${sooner} ${sooner === 1 ? 'month' : 'months'} earlier and leaves ${money(saved ?? 0)} of interest with you instead of the lender.`
                    : `Even ${money(50)} more a month would bring the end date forward. Give the extra payment slider a try.`
                : `On its own, your current payment would never clear this loan. Add the extra ${money(extra)} and it is paid off in ${formatDuration(result.boosted.months)}.`}
        />
    )
}

export const NetWorthCalculator = () => {
    const [cash, setCash] = useState(18000)
    const [investments, setInvestments] = useState(85000)
    const [property, setProperty] = useState(0)
    const [debts, setDebts] = useState(24000)
    const [monthly, setMonthly] = useState(800)

    const result = useMemo(() => {
        const months = 120
        const series: Array<number> = []

        for (let month = 0; month <= months; month += 1) {
            const investGrowth = growthSeries(investments, monthly, 6, month)
            const invested = investGrowth[investGrowth.length - 1]
            const savings = cash * Math.pow(1 + 0.02 / 12, month)
            const home = property * Math.pow(1 + 0.03 / 12, month)
            const owed = debts * Math.max(0, 1 - month / months)

            series.push(invested + savings + home - owed)
        }

        const assets = cash + investments + property

        return { series, today: assets - debts, future: series[series.length - 1], assets }
    }, [cash, investments, property, debts, monthly])

    const debtShare = result.assets + debts > 0 ? (debts / (result.assets + debts)) * 100 : 0

    return (
        <CalculatorLayout
            controls={(
                <>
                    <SliderField id='worth-cash' label='Cash in the bank' value={cash} min={0} max={250000} step={1000} onChange={setCash} format={money} />
                    <SliderField id='worth-investments' label='Investment and retirement accounts' value={investments} min={0} max={1500000} step={5000} onChange={setInvestments} format={money} />
                    <SliderField id='worth-property' label='Property value' value={property} min={0} max={2000000} step={10000} onChange={setProperty} format={money} />
                    <SliderField id='worth-debts' label='Everything you owe' value={debts} min={0} max={1000000} step={1000} onChange={setDebts} format={money} />
                    <SliderField id='worth-monthly' label='Monthly investing' value={monthly} min={0} max={5000} step={50} onChange={setMonthly} format={money} />

                    <div>
                        <p className='text-[13px] text-cream/65'>
                            Owned versus owed
                        </p>

                        <div className='mt-3 flex h-3 overflow-hidden rounded-full bg-white/[0.06]'>
                            <span className='h-full bg-[linear-gradient(90deg,#e2f37a,#8fae62)] transition-[width] duration-700 ease-cinema' style={{ width: `${100 - debtShare}%` }} />
                            <span className='h-full bg-[#e8b65d]/70 transition-[width] duration-700 ease-cinema' style={{ width: `${debtShare}%` }} />
                        </div>

                        <div className='mt-2 flex justify-between text-xs text-cream/50'>
                            <span>
                                You own {money(result.assets)}
                            </span>

                            <span>
                                You owe {money(debts)}
                            </span>
                        </div>
                    </div>
                </>
            )}
            results={(
                <>
                    <Result label='Net worth now' value={result.today} highlight />
                    <Result label='Ten years on' value={result.future} />
                    <Result label='Built up' value={result.future - result.today} />
                </>
            )}
            chart={(
                <ProjectionChart
                    series={[{ label: 'Expected net worth', values: resample(result.series.map(value => Math.max(0, value)), POINTS), area: true }]}
                    max={niceCeiling(Math.max(...result.series, 1) * 1.05)}
                    xLabels={['Now', 'Year 5', 'Year 10']}
                />
            )}
            insight={result.today < 0
                ? 'Right now you owe more than you own, which is normal early in life. Clearing the costliest debt first is the quickest way to turn it around.'
                : `This assumes investments grow 6% a year, cash 2% and property 3%, with debts repaid evenly over ten years.`}
        />
    )
}
